/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { useMemo, type ReactNode } from 'react';

// eslint-disable-next-line no-unused-vars -- `jsx` is required by the classic pragma above
import { cssMap, jsx } from '@compiled/react';

import BidiWarning from '@atlaskit/code/bidi-warning-ui';
import codeBidiWarningDecorator from '@atlaskit/code/bidi-warning/bidi-warning-decorator';
import { token } from '@atlaskit/tokens';

import type { Decoration } from '../types/decoration';
import type { HighlightedLine } from '../types/highlighted-line';
import type { LineNumberRenderInfo } from '../types/line-number';
import { applyDecorations } from '../utils/apply-decorations';

/**
 * Which visual theme a `CodeBlock` renders with.
 *
 * - `'ads'` (default): `@atlaskit/code`'s colours — its Prism token colours
 *   plus its `color.background.neutral` background, applied across both the
 *   gutter and the source with no divider, so a block reads consistently
 *   next to an ADS `CodeBlock`
 * - `'bitbucket'`: the theme this directory was ported with — Prism token
 *   colours from `prism-bitbucket.css`, an `elevation.surface` background,
 *   and a `color.border` divider between the gutter and the source
 *
 * Container geometry (border radius, block padding) is not part of either
 * theme; both leave it to the consumer.
 *
 * Named `ThemeName` rather than `CodeTheme` or `CodeBlockTheme` because
 * `@atlaskit/code` already exports both of those as objects of per-token
 * colour overrides, which is a different thing to a choice between two
 * ready-made themes.
 */
export type ThemeName = 'bitbucket' | 'ads';

const styles = cssMap({
	line: {
		display: 'flex',
	},
	firstLineCell: {
		paddingTop: token('space.100'),
	},
	lastLineCell: {
		paddingBottom: token('space.100'),
	},
	lineNumber: {
		position: 'sticky',
		left: 0,
		flex: '0 0 auto',
		minWidth: 'var(--bbc-code-line-number-width, var(--bbc-code-line-number-width-default))',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		textAlign: 'right',
		color: token('color.text.subtlest'),
		/*
		 * Exclude line numbers from text selection so copy/paste yields only
		 * the source content.
		 */
		userSelect: 'none',
	},
	lineNumberContent: {
		color: token('color.text.subtlest'),
		fontFamily: token('font.family.code'),
	},
	source: {
		flex: 1,
		paddingLeft: token('space.050'),
		whiteSpace: 'pre',
	},
});

/**
 * The gutter half of each theme. The source half — background and Prism token
 * colours — lives on the `<pre>` in `code-block.tsx`.
 *
 * The gutter needs its own background rather than inheriting the `<pre>`'s
 * because it is `position: sticky` and scrolls over the source horizontally.
 */
const lineNumberThemes = cssMap({
	bitbucket: {
		backgroundColor: token('elevation.surface'),
		borderRight: `1px solid ${token('color.border')}`,
	},
	/**
	 * `@atlaskit/code` paints the gutter with the same
	 * `color.background.neutral` as the source and draws no divider between
	 * them, so the gutter reads as part of one surface.
	 */
	ads: {
		backgroundColor: token('color.background.neutral'),
		borderRight: 'none',
	},
});

type CodeLineProps = {
	/** The syntax-highlighted tokens for this line. */
	line: HighlightedLine;
	/** Displayed line number rendered in the gutter. */
	lineNumber: number;
	/** Decorations applying to this line, pre-filtered. */
	decorations?: Decoration[];
	/** True if this is the first rendered line in the block. */
	isFirst?: boolean;
	/** True if this is the last rendered line in the block. */
	isLast?: boolean;
	/**
	 * When the consumer hasn't supplied a custom line-number renderer,
	 * the gutter is hidden from assistive tech. `undefined` (rather
	 * than `false`) keeps the attribute off the DOM entirely.
	 */
	isLineNumberAriaHidden?: true;
	/** Optional custom renderer for the line-number cell's contents. */
	renderLineNumber?: (info: LineNumberRenderInfo) => ReactNode;
	/**
	 * Which theme's gutter treatment to apply. Required, and always supplied by
	 * `CodeBlock`, so the gutter cannot end up on a different surface to the
	 * source it sits against.
	 */
	theme: ThemeName;
};

export function CodeLine({
	decorations,
	isFirst,
	isLast,
	isLineNumberAriaHidden,
	line,
	lineNumber,
	renderLineNumber,
	theme,
}: CodeLineProps): React.JSX.Element {
	const decoratedTokens = useMemo(
		() => applyDecorations({ decorations, line }),
		[decorations, line],
	);
	const styledLineNumber = <span css={styles.lineNumberContent}>{lineNumber}</span>;

	return (
		<span css={styles.line}>
			<span
				css={[
					styles.lineNumber,
					lineNumberThemes[theme],
					isFirst && styles.firstLineCell,
					isLast && styles.lastLineCell,
				]}
				aria-hidden={isLineNumberAriaHidden}
			>
				{renderLineNumber ? renderLineNumber({ lineNumber, styledLineNumber }) : styledLineNumber}
			</span>
			<span css={[styles.source, isFirst && styles.firstLineCell, isLast && styles.lastLineCell]}>
				{decoratedTokens.map((fragment, fragmentIndex) => {
					const Tag = fragment.tagName ?? 'span';
					return (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- structurally required: `fragment.className` carries the Prism `token …` classes emitted by the highlighter, which the syntax theme in `code-block.tsx` targets, plus any consumer `Decoration.className`. There is no co-located element to move these styles onto. Needs a `NoEslintDisableUiStylingStandardRules` ratchet exclusion agreed with #help-ui-styling-standard.
						<Tag key={fragmentIndex} className={fragment.className} style={fragment.style}>
							{codeBidiWarningDecorator(fragment.text, ({ bidiCharacter, index: bidiIndex }) => (
								<BidiWarning
									key={bidiIndex}
									bidiCharacter={bidiCharacter}
									shouldSkipChildren
									isTooltipEnabled
								/>
							))}
						</Tag>
					);
				})}
				{/*
				 * Render the trailing newline as a text node outside the
				 * token spans so users can copy/paste the original line
				 * breaks without altering the highlighted tokens.
				 */}
				{!isLast && '\n'}
			</span>
		</span>
	);
}
